import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import "../FreeAssesment/loader.css";
import logo from "../../assets/ExamWarriorLogo.svg";

function MockTestPage() {
    const { subject } = useParams();
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [timer, setTimer] = useState(subject === "reading" ? 45 * 60 : 40 * 60);
    const [answers, setAnswers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const navigate = useNavigate();
    const isResizing = useRef(false);
    const startX = useRef(0);
    const location = useLocation();

    const user = JSON.parse(localStorage.getItem('user'));
    const childId = user?.id || "";

    // Determine student type from user object
    const studentType = user?.is_selective ? 'selective' : user?.is_oc ? 'oc' : 'selective';

    const handleMouseDown = (e) => {
        isResizing.current = true;
        startX.current = e.clientX;
        document.body.style.cursor = "col-resize";
    };

    useEffect(() => {
        window.history.pushState(null, null, window.location.pathname);
        const preventBackNavigation = () => {
            window.history.pushState(null, null, window.location.pathname);
            alert("Back navigation is disabled during the test.");
        };

        window.addEventListener("popstate", preventBackNavigation);
        return () => window.removeEventListener("popstate", preventBackNavigation);
    }, []);

    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        handleSubmit();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [timer]);

    const handleButtonClick = () => {
        setShowPopup(true);
    };

    const handleCancel = () => {
        setShowPopup(false);
    };

    const handleConfirm = () => {
        setShowPopup(false);
        handleSubmit();
    };

    const [testTypeInfo, setTestTypeInfo] = useState({
        isPackageTest: false,
        isIndividualTest: false,
        testId: null,
        testNumber: 1
    });

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                const { testId, packageTest, individualTest, testNumber } = location.state || {};

                // Build URL with query parameters
                let url = `${process.env.REACT_APP_BACKEND_URL}/api/mock-test/${subject}`;
                const params = new URLSearchParams();

                if (packageTest && testId) {
                    params.append('packageTest', 'true');
                    params.append('testId', testId);

                    // Use testNumber from state if available, otherwise extract from testId
                    if (testNumber) {
                        params.append('testNumber', testNumber);
                    } else {
                        const match = testId.match(/mock-package-(\d+)/);
                        if (match) {
                            params.append('testNumber', match[1]);
                        }
                    }
                }

                if (individualTest) {
                    params.append('individualTest', 'true');
                    if (testNumber) {
                        params.append('testNumber', testNumber);
                    }
                }

                // Always append student type for filtering
                params.append('studentType', studentType);

                if (params.toString()) {
                    url += `?${params.toString()}`;
                }

                console.log('📡 Fetching questions from:', url);

                const response = await axios.get(url);

                if (!response.data || !Array.isArray(response.data.questions)) {
                    throw new Error("Invalid API response. Questions not found.");
                }

                setQuestions(response.data.questions);
                setAnswers(new Array(response.data.questions.length).fill(null));

                // Store test type information
                let extractedTestNumber = testNumber || 1;
                if (!extractedTestNumber && packageTest && testId) {
                    const match = testId.match(/mock-package-(\d+)/);
                    if (match) {
                        extractedTestNumber = parseInt(match[1]);
                    }
                }

                setTestTypeInfo({
                    isPackageTest: packageTest || false,
                    isIndividualTest: individualTest || false,
                    testId: testId || null,
                    testNumber: extractedTestNumber
                });

                setLoading(false);
            } catch (error) {
                console.error("Error fetching questions:", error);
                setLoading(false);
                toast.error("Failed to load questions. Please try again.");
            }
        };

        fetchQuestions();
    }, [subject, location.state, studentType]);

    const formatExtractText = (text) => {
        if (!text) return "";
        const extractRegex = /\b(Extract\s+[A-Z]|Text\s+[A-Z])\b/g;
        const formattedText = text.replace(
            extractRegex,
            (match) => `<br><br><b>${match}</b><br>`
        );
        const lines = formattedText
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line.length > 0);

        const introText = `<p><strong>${lines[0]}</strong></p>`;
        let poemLabel = "";
        if (lines.length > 1 && lines[1].toLowerCase().startsWith("poem")) {
            poemLabel = `<p><strong>${lines[1]}</strong></p>`;
        }
        const poemContent = lines
            .slice(poemLabel ? 2 : 1)
            .map((line) => `<p><strong>${line}</strong></p>`)
            .join("");
        return introText + poemLabel + poemContent;
    };

    const handleSelectAnswer = (value) => {
        const updatedAnswers = [...answers];
        updatedAnswers[currentIndex] = {
            questionId: questions[currentIndex].id,
            selectedAnswer: value,
        };
        setAnswers(updatedAnswers);
    };

    const handleWritingAnswer = (event) => {
        const inputText = event.target.value;
        const wordCount = inputText.trim().split(/\s+/).length;
        if (wordCount > 300) {
            toast.error("Your response is too long. Please limit to 300 words.");
            return;
        }
        setAnswers((prev) => ({
            ...prev,
            writing: event.target.value
        }));
    };

    const handleSubmit = async () => {
    setIsLoading(true);

    if (!questions.length) {
        toast.error("No questions to submit.");
        return;
    }

    const questionStatus = questions.map((question, index) => ({
        questionNumber: index + 1,
        status: answers[index] ? "attempted" : "unattempted",
    }));

    const isWritingTest = subject === "writing";

    const endpoint = isWritingTest
        ? "/api/mock-test/submit-writing"
        : "/api/mock-test/submit";

    const payload = {
        childId,
        subject,
        testType: "mock",
        score: isWritingTest ? null : calculateScore(),
        questionStatus,
        responses: isWritingTest
            ? { writing: answers.writing }
            : answers.filter(ans => ans !== null),
        packageTest: testTypeInfo.isPackageTest,
        individualTest: testTypeInfo.isIndividualTest,
        testId: testTypeInfo.testId,
        testNumber: testTypeInfo.testNumber
    };

    try {
        const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}${endpoint}`,
            payload
        );

        if (isWritingTest) {
            toast.success("Writing test submitted for review! It will be graded by a teacher.");
        } else {
            toast.success("Test submitted successfully!");
        }

        // ✅ FIXED: Navigate back to the correct individual subject page with proper subject mapping
        if (testTypeInfo.isIndividualTest) {
            // Map the subject from URL params to the correct format for IndividualMockTestsPage
            const subjectMapping = {
                'maths': 'Maths',
                'thinkingskills': 'ThinkingSkills',
                'writing': 'Writing',
                'reading': 'Reading'
            };

            const displayNameMapping = {
                'maths': 'Mathematical Reasoning',
                'thinkingskills': 'Thinking Skills',
                'writing': 'Writing',
                'reading': 'Reading'
            };

            // Get the formatted subject key from URL params
            const formattedSubject = subjectMapping[subject.toLowerCase()] || 'Maths';
            const displayName = displayNameMapping[subject.toLowerCase()] || 'Mathematical Reasoning';

            setTimeout(() => {
                navigate("/individual-mock-test", {
                    state: {
                        subject: formattedSubject, // This should match the keys in subjectConfig
                        subjectDisplayName: displayName,
                        testType: 'individual'
                    }
                });
            }, 2000);
        } else {
            setTimeout(() => navigate("/mock-tests"), 2000);
        }
    } catch (error) {
        toast.error("Error submitting test.");
        console.error("Submission Error:", error);
    } finally {
        setIsLoading(false);
    }
};

    const calculateScore = () => {
        if (subject === "writing") return null;

        return answers.reduce((acc, ans, index) => {
            if (!ans || !ans.selectedAnswer) return acc;
            const submittedAnswer = ans.selectedAnswer.trim();
            const correctAnswer = questions[index]?.correct_answer?.trim();
            if (!correctAnswer) return acc;
            const isCorrect = isAnswerCorrect(submittedAnswer, correctAnswer, questions[index]?.mcq_options);
            return isCorrect ? acc + 1 : acc;
        }, 0);
    };

    const isAnswerCorrect = (selected, correct, optionsString) => {
        if (!selected || !correct) return false;

        const normalizeAnswer = (answer) => {
            return answer.toString()
                .trim()
                .toLowerCase()
                .replace(/\s+/g, ' ')
                .replace(/\s*hrs?\s*/g, 'hr ')
                .replace(/\s*min\s*/g, 'min ')
                .replace(/\s*cm\s*/g, 'cm ')
                .replace(/\s+/g, ' ')
                .trim();
        };

        const normalizedSelected = normalizeAnswer(selected);
        const normalizedCorrect = normalizeAnswer(correct);

        if (normalizedSelected === normalizedCorrect) return true;

        if (/^[a-e]$/.test(normalizedSelected) && /^[a-e]$/.test(normalizedCorrect)) {
            return normalizedSelected === normalizedCorrect;
        }

        if (optionsString) {
            const options = parseMcqOptions(optionsString);
            const normalizedOptions = options.map(opt => ({
                ...opt,
                normalizedText: normalizeAnswer(opt.text)
            }));

            if (/^[a-e]$/.test(normalizedSelected)) {
                const selectedOption = normalizedOptions.find(opt => opt.letter.toLowerCase() === normalizedSelected);
                if (selectedOption && selectedOption.normalizedText === normalizedCorrect) {
                    return true;
                }
            }

            if (/^[a-e]$/.test(normalizedCorrect)) {
                const correctOption = normalizedOptions.find(opt => opt.letter.toLowerCase() === normalizedCorrect);
                if (correctOption && correctOption.normalizedText === normalizedSelected) {
                    return true;
                }
            }

            const matchingOption = normalizedOptions.find(opt =>
                opt.normalizedText === normalizedSelected
            );
            if (matchingOption) {
                if (/^[a-e]$/.test(normalizedCorrect) && normalizedCorrect === matchingOption.letter.toLowerCase()) {
                    return true;
                }
                if (normalizedCorrect === matchingOption.normalizedText) {
                    return true;
                }
            }

            const correctMatchingOption = normalizedOptions.find(opt =>
                opt.normalizedText === normalizedCorrect
            );
            if (correctMatchingOption) {
                if (/^[a-e]$/.test(normalizedSelected) && normalizedSelected === correctMatchingOption.letter.toLowerCase()) {
                    return true;
                }
                if (normalizedSelected === correctMatchingOption.normalizedText) {
                    return true;
                }
            }
        }

        return selected.toString().trim().toLowerCase() === correct.toString().trim().toLowerCase();
    };

    const parseMcqOptions = (optionsString) => {
        if (!optionsString) return [];
        const options = [];
        const parts = optionsString.split(/,\s(?=[A-Z]\s)/);
        parts.forEach(part => {
            if (part.trim()) {
                const spaceIndex = part.indexOf(' ');
                if (spaceIndex > 0) {
                    const letter = part.substring(0, spaceIndex).trim();
                    let text = part.substring(spaceIndex + 1).trim();
                    if (letter === 'E') {
                        // Keep as is
                    } else if (text.endsWith(',')) {
                        text = text.slice(0, -1).trim();
                    }
                    if (letter && text) {
                        options.push({ letter, text });
                    }
                }
            }
        });
        return options;
    };

    const handleNext = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center">
                <div className="loadingspinner">
                    <div id="square1"></div>
                    <div id="square2"></div>
                    <div id="square3"></div>
                    <div id="square4"></div>
                    <div id="square5"></div>
                </div>
            </div>
        );
    }

    if (!questions.length) {
        return (
            <div className="h-screen flex items-center justify-center">
                <p>No questions found. Please try again later.</p>
            </div>
        );
    }

    const currentQuestion = questions[currentIndex];
    const mcqOptions = currentQuestion?.mcq_options ? parseMcqOptions(currentQuestion.mcq_options) : [];
    const minutes = Math.floor(timer / 60);
    const seconds = String(timer % 60).padStart(2, "0");
    const totalQuestions = questions.length;

    return (
        <div className="h-screen bg-white flex flex-col">
            {/* Header with Timer */}
            <div className="absolute w-full h-20 md:h-24 flex justify-between items-center py-1 px-3 md:px-6 bg-white shadow-md z-10">
                <img src={logo} alt="img-logo" className="h-16 w-24 md:h-24 md:w-32" />
                <div className="flex justify-center items-center bg-black text-white text-lg md:text-xl font-bold p-1 shadow-lg">
                    <div className="flex flex-col items-center mx-1 md:mx-2">
                        <span>{minutes}</span>
                        <span className="text-xs md:text-sm text-gray-400 uppercase tracking-wide">
                            Min
                        </span>
                    </div>
                    <span className="text-2xl md:text-4xl mx-1 md:mx-2">:</span>
                    <div className="flex flex-col items-center mx-1 md:mx-2">
                        <span>{seconds}</span>
                        <span className="text-xs md:text-sm text-gray-400 uppercase tracking-wide">
                            Sec
                        </span>
                    </div>
                </div>
            </div>

            <div className="flex-grow flex flex-col md:flex-row pt-20 overflow-hidden">
                <div className="w-full md:w-1/2 bg-white p-6 overflow-y-auto max-h-[calc(100vh-120px)]">
                    <p className="mb-3 sm:mb-4">
                        <span className="block bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent text-xl sm:text-2xl font-extrabold tracking-wider uppercase mb-3 drop-shadow-md border-b-2 border-purple-300 pb-1">
                            Question {currentIndex + 1} of {totalQuestions}
                        </span>

                        <div className="text-xl sm:text-2xl font-bold text-gray-900 leading-relaxed">
                            {currentQuestion?.question ? (
                                currentQuestion.question.split('\n').map((line, index) => (
                                    <div key={index} className="mb-2">
                                        {line}
                                    </div>
                                ))
                            ) : (
                                "No question available"
                            )}
                        </div>
                    </p>

                    {currentQuestion.image_data && (
                        <div className="mt-4">
                            <img
                                src={
                                    currentQuestion.image_data.startsWith("http")
                                        ? currentQuestion.image_data
                                        : `http://${currentQuestion.image_data}`
                                }
                                alt="Question Image"
                                className="max-w-full rounded-md shadow-lg"
                            />
                            {currentQuestion.image_description && (
                                <p className="text-gray-600 text-xl pt-4">
                                    {currentQuestion.image_description}
                                </p>
                            )}
                        </div>
                    )}

                    {subject === "reading" && currentQuestion?.extract_text && (
                        <div className="mt-4">
                            <p
                                className="text-black text-base md:text-lg leading-relaxed"
                                dangerouslySetInnerHTML={{
                                    __html: formatExtractText(currentQuestion.extract_text),
                                }}
                            ></p>
                        </div>
                    )}
                </div>

                <div
                    className="resize-handle hidden md:block"
                    onMouseDown={handleMouseDown}
                    style={{
                        cursor: "col-resize",
                        backgroundColor: "gray",
                        width: "3px",
                        height: "auto",
                    }}
                ></div>

                <div className="w-full md:w-1/2 bg-white pb-32 md:p-6">
                    <div className="space-y-6 mt-10">
                        {subject === "writing" ? (
                            <div>
                                <textarea
                                    className="w-full p-2 border rounded"
                                    rows="10"
                                    placeholder="Write your answer here..."
                                    onChange={handleWritingAnswer}
                                    value={answers.writing || ""}
                                ></textarea>
                                <p className="text-gray-600 mt-2">
                                    Word Count: {answers.writing ? answers.writing.trim().split(/\s+/).length : 0} / 300
                                </p>
                            </div>
                        ) : (mcqOptions.map((option, index) => (
                            <label
                                key={index}
                                className="flex items-center space-x-2 py-4 px-2 bg-gray-200 border border-gray-300 rounded-lg"
                            >
                                <input
                                    type="radio"
                                    name={`question-${currentIndex}`}
                                    value={option.letter}
                                    className="peer h-8 w-8 focus:ring-yellow-600 border-gray-300"
                                    onChange={() => handleSelectAnswer(option.letter)}
                                    checked={answers[currentIndex]?.selectedAnswer === option.letter}
                                />
                                <span className="text-black text-lg font-semibold">
                                    {option.text}
                                </span>
                            </label>
                        ))
                        )}
                    </div>
                </div>
            </div>

            <div className="relative bottom-0 left-0 right-0 bg-blue-500 text-white py-3 flex justify-between z-30">
                <button
                    onClick={handlePrevious}
                    className={`p-2 px-4 rounded-md font-semibold bg-black ml-2 ${currentIndex === 0 ? "opacity-50" : ""
                        }`}
                    disabled={currentIndex === 0}
                >
                    &lt;&lt; PREVIOUS
                </button>

                <button
                    onClick={handleNext}
                    className={`p-2 px-4 rounded-md font-semibold bg-black mr-3 ${currentIndex === questions.length - 1 ? "opacity-50" : ""
                        }`}
                    disabled={currentIndex === questions.length - 1}
                >
                    NEXT &gt;&gt;
                </button>
                {(currentIndex === questions.length - 1) &&
                    (<button
                        onClick={handleButtonClick}
                        className="bg-black text-white font-bold p-2 px-6 mr-3 rounded-md shadow-lg hover:scale-105 transform transition-all duration-300 ease-in-out"
                    >
                        Submit Test!
                    </button>
                    )}
            </div>

            {showPopup && (
                <div className="fixed z-50 inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="relative bg-white rounded-lg shadow-lg overflow-hidden transform transition-all w-full max-w-md sm:mx-auto">
                        <button
                            onClick={handleCancel}
                            type="button"
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                            <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="px-6 py-8">
                            <div className="flex items-center space-x-4">
                                <div className="flex-shrink-0">
                                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                                        <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Confirm Submission
                                </h3>
                            </div>

                            <div className="mt-4 text-gray-700">
                                Are you sure you want to submit the mock test?
                            </div>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    onClick={handleCancel}
                                    type="button"
                                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    type="button"
                                    className="px-4 py-2 text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isLoading && (
                <div className="fixed inset-0 flex justify-center items-center bg-gray-500 bg-opacity-50 z-50">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
                    <span className="ml-4 text-white">
                        Your test is being submitted....
                    </span>
                </div>
            )}

            <ToastContainer />
        </div>
    );
}

export default MockTestPage;